import { match, P } from '@st/pattern'
import type { CourseificVideo } from './types'

/** Owns the Bunny iframe and its Player.js message subscription. */
export function mountBunnyPlayer(
  container: HTMLDivElement,
  video: CourseificVideo,
  title: string,
  onCompleted: () => void
) {
  const player = document.createElement('iframe')
  const origin = 'https://player.mediadelivery.net'
  const watchedSeconds = new Set<number>()
  let playing = false
  let completed = false

  function reportCompleted() {
    if (completed) return
    completed = true
    onCompleted()
  }

  // Bunny implements Player.js over postMessage. Keep the subscription tied to
  // this iframe so a previous lesson cannot complete the newly selected one.
  function handleMessage(event: MessageEvent) {
    if (event.source != player.contentWindow || event.origin != origin) return
    // Chrome iOS also sends object-valued frame-registration messages from this
    // iframe. Player.js events use JSON strings.
    if (typeof event.data != 'string') return
    const message = JSON.parse(event.data)
    if (message.context != 'player.js') return

    match(message)
      .with({ event: 'ready' }, () => {
        for (const value of ['play', 'pause', 'timeupdate', 'ended']) {
          player.contentWindow!.postMessage(
            JSON.stringify({
              context: 'player.js',
              version: '0.0.11',
              method: 'addEventListener',
              value,
              listener: value
            }),
            origin
          )
        }
      })
      .with({ event: 'play' }, () => {
        playing = true
      })
      .with({ event: 'pause' }, () => {
        playing = false
      })
      .with({ event: 'ended' }, reportCompleted)
      .with(
        { event: 'timeupdate', value: { seconds: P.number, duration: P.number } },
        ({ value: { seconds, duration } }) => {
          if (!playing || duration <= 0) return
          // Count distinct playback seconds: replaying a section or seeking
          // ahead must not count the skipped portion as watched.
          watchedSeconds.add(Math.floor(seconds))
          if (watchedSeconds.size / Math.ceil(duration) >= 0.9) reportCompleted()
        }
      )
      .otherwise(() => {})
  }

  window.addEventListener('message', handleMessage)
  player.src = `${origin}/embed/${video.libraryId}/${video.videoId}?autoplay=false`
  player.title = title
  player.allow =
    'accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen'
  player.allowFullscreen = true
  player.className = 'block aspect-video w-full border-0'
  container.replaceChildren(player)

  return () => {
    window.removeEventListener('message', handleMessage)
    container.replaceChildren()
  }
}
